/*
  DUK Rabattcode im Warenkorb-Drawer (Fenster „Rabattcode“ neben „Anmerkung“, sections/cart-drawer.liquid)
  ------------------------------------------------------------------------------------------------
  Eigenes Feld statt des Theme-Felds, weil das Theme nur beim Verlassen des Felds einlöst und bei ungültigen
  Codes oft nichts anzeigt (Matthias, 04.10.: „am Desktop kam keine Bestätigung oder Response“).
  - Enter oder „Einlösen“ löst ein. Enter schickt das Formular nicht mehr an die Kasse.
  - Gültig: Hinweis „Rabattcode … eingelöst.“, das Fenster schließt, der Drawer lädt neu und zeigt den Code
    über dem Gesamtbetrag.
  - Nicht gültig (auch Geschenkgutscheine, die nur der Checkout annimmt): Meldung im Fenster, der Code wird
    wieder aus dem Warenkorb genommen, damit er nicht in den Checkout mitwandert.
  - Abgelaufen (11.10., Matthias): Codes aus dem Shop-Metafeld custom.expired_discount_codes bekommen sofort
    „„…“ ist abgelaufen.“ und werden gar nicht erst in den Warenkorb gelegt. Die Liste kommt über
    ?section_id=duk-expired-codes (sections/duk-expired-codes.liquid), abgerufen erst beim Einlösen.
    Neue abgelaufene Codes nur im Metafeld eintragen (Admin: Settings → Custom data → Shop), kein Theme-Update nötig.
  Texte kommen als data-msg-* aus Liquid ({code} wird ersetzt).
*/

const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));

const sameCode = (a, b) => String(a || '').toLowerCase() === String(b || '').toLowerCase();

const isGerman = () => String(document.documentElement.lang || '').toLowerCase().startsWith('de');

// Abgelaufene Codes, einmal pro Seitenaufruf geladen (Shop-Metafeld custom.expired_discount_codes)
let expiredRequest = null;
const expiredCodes = () => {
  if (!expiredRequest) {
    expiredRequest = fetch(`${root()}?section_id=duk-expired-codes`, { headers: { Accept: 'text/html' } })
      .then((response) => {
        if (!response.ok) throw new Error(String(response.status));
        return response.text();
      })
      .then((html) => {
        const node = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-duk-expired]');
        return (node ? node.getAttribute('data-duk-expired') || '' : '')
          .split(',')
          .map((entry) => entry.trim())
          .filter(Boolean);
      })
      .catch(() => {
        expiredRequest = null;
        return [];
      });
  }
  return expiredRequest;
};

class DukCartDiscount extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;

    this.input = this.querySelector('input:not([type="hidden"])');
    this.button = this.querySelector('[data-apply] button');
    this.error = this.querySelector('[data-error]');
    if (!this.input) return;

    this.input.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      this.apply();
    });

    this.input.addEventListener('input', () => this.showError(''));

    if (this.button) {
      this.button.addEventListener('click', (event) => {
        event.preventDefault();
        this.apply();
      });
    }
  }

  async isExpired(code) {
    const list = await expiredCodes();
    return list.some((entry) => sameCode(entry, code));
  }

  expiredText(code) {
    const fallback = isGerman() ? '„{code}“ ist abgelaufen.' : '“{code}” has expired.';
    return (this.dataset.msgExpired || fallback).split('{code}').join(code);
  }

  text(key, code = '') {
    return (this.dataset[key] || '').split('{code}').join(code);
  }

  showError(message) {
    if (!this.error) return;
    this.error.textContent = message;
    this.error.hidden = !message;
  }

  async post(discount) {
    const response = await fetch(`${root()}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount })
    });

    if (!response.ok) throw new Error(String(response.status));
    return response.json();
  }

  async apply() {
    const code = this.input.value.trim();
    if (!code || this.busy) return;

    this.busy = true;
    if (this.button) this.button.setAttribute('aria-busy', 'true');
    this.showError('');

    try {
      if (await this.isExpired(code)) {
        this.showError(this.expiredText(code));
        return;
      }

      const cart = await (await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } })).json();
      const active = (cart.discount_codes || []).filter((entry) => entry.applicable).map((entry) => entry.code);

      if (active.some((entry) => sameCode(entry, code))) {
        this.showError(this.text('msgActive', code));
        return;
      }

      const updated = await this.post([...active, code].join(','));
      const entry = (updated.discount_codes || []).find((item) => sameCode(item.code, code));

      if (entry && entry.applicable) {
        this.done(code);
        return;
      }

      // Not accepted: take it out again, the other codes stay
      if (entry) await this.post(active.join(','));
      this.showError(this.text('msgInvalid', code));
    } catch (error) {
      this.showError(this.text('msgError'));
    } finally {
      this.busy = false;
      if (this.button) this.button.setAttribute('aria-busy', 'false');
    }
  }

  // Close the window first, then let the theme reload the drawer (it shows the code above the total)
  done(code) {
    document.dispatchEvent(new CustomEvent('toast:show', { detail: { message: escapeHtml(this.text('msgApplied', code)), duration: 3000 } }));

    const refresh = () => document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    const popover = this.closest('x-drawer-popover');

    if (popover && typeof popover.hide === 'function') {
      Promise.resolve()
        .then(() => popover.hide())
        .catch(() => {})
        .then(() => window.setTimeout(refresh, 50));
    } else {
      refresh();
    }
  }
}

if (!window.customElements.get('duk-cart-discount')) {
  window.customElements.define('duk-cart-discount', DukCartDiscount);
}
